import { forwardRef } from "react";

interface RehearsalCardProps {
  tactics: string[];
  readinessScore: number;
  exchangeCount: number;
  masterGreyRock?: string;
}

const RehearsalCard = forwardRef<HTMLDivElement, RehearsalCardProps>(
  ({ tactics, readinessScore, exchangeCount, masterGreyRock }, ref) => {
    const amber = "#F5A623";

    return (
      <div
        ref={ref}
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: 1080,
          height: 1920,
          background: "#000000",
          fontFamily: "'Inter', system-ui, sans-serif",
          boxSizing: "border-box",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: "72px 60px 56px",
        }}
      >
        {/* Subtle ambient glow */}
        <div
          style={{
            position: "absolute",
            top: "42%",
            left: "50%",
            width: 800,
            height: 800,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${amber}08 0%, transparent 65%)`,
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />

        {/* ── Top branding ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 48,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: 22,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: "#AAAAAA",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            GreyRockAI
          </span>
          <span
            style={{
              fontSize: 20,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#AAAAAA",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            Rehearsal
          </span>
        </div>

        {/* ── Quote ── */}
        <div
          style={{
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginBottom: 48,
            marginTop: 24,
          }}
        >
          <p
            style={{
              fontSize: 44,
              fontStyle: "italic",
              color: "#ffffff",
              textAlign: "center",
              lineHeight: 1.35,
              maxWidth: 960,
              fontWeight: 400,
              whiteSpace: "nowrap",
            }}
          >
            "I practiced this before it was real. 🪨"
          </p>
        </div>

        {/* ── Tactics I Faced ── */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 28,
          }}
        >
          <span
            style={{
              fontSize: 24,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: amber,
              fontWeight: 700,
            }}
          >
            Tactics I Faced
          </span>
          <div
            style={{
              width: 160,
              height: 3,
              background: amber,
              borderRadius: 2,
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 18,
            }}
          >
            {tactics.slice(0, 3).map((tactic, i) => (
              <span
                key={i}
                style={{
                  fontSize: 46,
                  fontWeight: 800,
                  color: "#ffffff",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {tactic}
              </span>
            ))}
          </div>
        </div>

        {/* ── Score displays ── */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 100,
            marginTop: 48,
            marginBottom: 48,
            flexShrink: 0,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                fontSize: 84,
                fontWeight: 800,
                color: amber,
                display: "block",
                lineHeight: 1,
              }}
            >
              {readinessScore}
            </span>
            <span
              style={{
                fontSize: 18,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "#AAAAAA",
                marginTop: 28,
                display: "block",
              }}
            >
              Readiness Score
            </span>
          </div>
          <div style={{ textAlign: "center" }}>
            <span
              style={{
                fontSize: 84,
                fontWeight: 800,
                color: amber,
                display: "block",
                lineHeight: 1,
              }}
            >
              {exchangeCount}
            </span>
            <span
              style={{
                fontSize: 18,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "#AAAAAA",
                marginTop: 28,
                display: "block",
              }}
            >
              Exchanges
            </span>
          </div>
        </div>

        {/* ── Master Grey Rock ── */}
        {masterGreyRock && (
          <div
            style={{
              flexShrink: 0,
              textAlign: "center",
              marginBottom: 32,
              padding: "0 20px",
            }}
          >
            <span
              style={{
                fontSize: 18,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: amber,
                fontWeight: 700,
                display: "block",
                marginBottom: 16,
              }}
            >
              Master Grey Rock
            </span>
            <p
              style={{
                fontSize: 28,
                fontStyle: "italic",
                color: "#ffffff",
                lineHeight: 1.4,
                maxWidth: 900,
                margin: "0 auto",
              }}
            >
              "{masterGreyRock}"
            </p>
          </div>
        )}

        {/* ── Footer ── */}
        <div
          style={{
            flexShrink: 0,
            textAlign: "center",
          }}
        >
          <span
            style={{
              fontSize: 16,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.60)",
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            REHEARSE YOURS → GREYROCKAI.APP®
          </span>
        </div>
      </div>
    );
  }
);

RehearsalCard.displayName = "RehearsalCard";

export default RehearsalCard;
